type RequestIdleCallbackFallback = (deadline: {
  didTimeout: boolean;
  timeRemaining: () => number;
}) => void;
type RequestIdleCallbackOptionsFallback = { timeout?: number };
type WindowWithIdleCallback = {
  requestIdleCallback?: (
    callback: RequestIdleCallbackFallback,
    options?: RequestIdleCallbackOptionsFallback
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
};

const fallbackRequestIdleCallback = (cb: RequestIdleCallbackFallback): number => {
  const start = Date.now();

  return window.setTimeout(() => {
    cb({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1);
};

const fallbackCancelIdleCallback = (handle: number): void => {
  window.clearTimeout(handle);
};

const browserWindow = window as unknown as WindowWithIdleCallback;

browserWindow.requestIdleCallback =
  browserWindow.requestIdleCallback || fallbackRequestIdleCallback;
browserWindow.cancelIdleCallback = browserWindow.cancelIdleCallback || fallbackCancelIdleCallback;

if (process.env.NODE_ENV === "development") {
  const resizeObserverLoopErrorMessages = new Set([
    "ResizeObserver loop completed with undelivered notifications.",
    "ResizeObserver loop limit exceeded"
  ]);

  const isResizeObserverLoopError = (message: unknown): boolean => {
    return typeof message === "string" && resizeObserverLoopErrorMessages.has(message);
  };

  const suppressResizeObserverLoopError = (event: ErrorEvent): void => {
    if (!isResizeObserverLoopError(event.message)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const suppressResizeObserverLoopRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : reason;

    if (!isResizeObserverLoopError(message)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  window.addEventListener("error", suppressResizeObserverLoopError, true);
  window.addEventListener("unhandledrejection", suppressResizeObserverLoopRejection, true);
}

export {};
